import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { DollarSign, FileText, Calendar, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { quotesAPI, invoicesAPI, hrAPI } from '../lib/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatCurrency } from '../utils/currency';
import StripeConnectPanel from '../components/payments/StripeConnectPanel';

// Safe date formatter — falls back to '—' for missing/invalid dates (avoids "Invalid Date").
const formatPaymentDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function Payments() {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, quote: 0, invoice: 0 });
  const [filter, setFilter] = useState('All'); // 'All', 'Quote', 'Invoice'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [teamMembersForCreatedBy, setTeamMembersForCreatedBy] = useState([]);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      // Fetch all quotes and invoices using API wrapper
      const [quotesResponse, invoicesResponse] = await Promise.all([
        quotesAPI.getAll(),
        invoicesAPI.getAll()
      ]);

      const quotesRaw = quotesResponse?.data;
      const quotes = Array.isArray(quotesRaw) ? quotesRaw : (quotesRaw?.items || quotesRaw?.data || []);
      const invoicesRaw = invoicesResponse?.data;
      const invoices = Array.isArray(invoicesRaw) ? invoicesRaw : (invoicesRaw?.items || invoicesRaw?.data || []);

      // Fetch payments for each quote and invoice
      let quotePayments = [];
      let invoicePayments = [];

      // Fetch payments for all quotes and invoices
      if (quotes.length > 0) {
        const quotePaymentsPromises = quotes.map(async (quote) => {
          try {
            const response = await quotesAPI.getPayments(quote.id);
            const d = response?.data;
            return Array.isArray(d) ? d : (d?.payments || []);
          } catch (error) {
            console.error(`Failed to fetch payments for quote ${quote.id}:`, error);
            return [];
          }
        });
        const quotePaymentsArrays = await Promise.all(quotePaymentsPromises);
        quotePayments = quotePaymentsArrays.flat();
      }

      if (invoices.length > 0) {
        const invoicePaymentsPromises = invoices.map(async (invoice) => {
          try {
            const response = await invoicesAPI.getPayments(invoice.id);
            const d = response?.data;
            return Array.isArray(d) ? d : (d?.payments || []);
          } catch (error) {
            console.error(`Failed to fetch payments for invoice ${invoice.id}:`, error);
            return [];
          }
        });
        const invoicePaymentsArrays = await Promise.all(invoicePaymentsPromises);
        invoicePayments = invoicePaymentsArrays.flat();
      }
      
      
      // Create lookup maps for quote/invoice numbers + currency
      const quoteMap = {};
      const quoteCurrencyMap = {};
      quotes.forEach(q => { quoteMap[q.id] = q.quote_number; quoteCurrencyMap[q.id] = q.currency; });

      const invoiceMap = {};
      const invoiceCurrencyMap = {};
      invoices.forEach(i => { invoiceMap[i.id] = i.invoice_number; invoiceCurrencyMap[i.id] = i.currency; });

      // Combine and format payments
      const allPayments = [
        ...quotePayments.map(p => ({ 
          ...p, 
          type: 'Quote', 
          source_id: p.quote_id,
          source_number: quoteMap[p.quote_id] || 'Unknown',
          currency: quoteCurrencyMap[p.quote_id] || 'USD',
          date: p.payment_date || p.created_at,
          method: p.payment_method,
          reference: p.transaction_id
        })),
        ...invoicePayments.map(p => ({ 
          ...p, 
          type: 'Invoice', 
          source_id: p.invoice_id,
          source_number: invoiceMap[p.invoice_id] || 'Unknown',
          currency: invoiceCurrencyMap[p.invoice_id] || 'USD',
          date: p.payment_date || p.created_at,
          method: p.payment_method,
          reference: p.transaction_id
        })),
      ];

      // Sort by date
      allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Calculate stats
      const total = allPayments.reduce((sum, p) => sum + p.amount, 0);
      const quote = quotePayments.reduce((sum, p) => sum + p.amount, 0);
      const invoice = invoicePayments.reduce((sum, p) => sum + p.amount, 0);

      setPayments(allPayments);
      setStats({ total, quote, invoice });
      
      // Load team members for Created By column
      try {
        const teamMembersRes = await hrAPI.getTeamMembers();
        setTeamMembersForCreatedBy(teamMembersRes.data || []);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load payments:', error);
      toast.error('Failed to load payments');
      setLoading(false);
    }
  };

  // Filter payments by date range and type
  const getFilteredPayments = () => {
    let filtered = payments;

    // Filter by type
    if (filter !== 'All') {
      filtered = filtered.filter(p => p.type === filter);
    }

    // Filter by date range
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(p => new Date(p.date) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(p => new Date(p.date) <= end);
    }

    return filtered;
  };

  // Calculate filtered stats
  const getFilteredStats = () => {
    const filtered = getFilteredPayments();
    
    const total = filtered.reduce((sum, p) => sum + p.amount, 0);
    const quote = filtered.filter(p => p.type === 'Quote').reduce((sum, p) => sum + p.amount, 0);
    const invoice = filtered.filter(p => p.type === 'Invoice').reduce((sum, p) => sum + p.amount, 0);

    return { total, quote, invoice };
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setFilter('All');
  };

  const hasActiveFilters = startDate || endDate || filter !== 'All';
  const filteredStats = hasActiveFilters ? getFilteredStats() : stats;
  const filteredPayments = getFilteredPayments();

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Payments</h1>
        {hasActiveFilters && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={clearFilters}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}
      </div>

      {/* Accept Online Payments — Stripe Connect */}
      <StripeConnectPanel />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="From date"
                data-testid="payment-start-date"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="To date"
                data-testid="payment-end-date"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Type</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-2 border rounded-md bg-background"
                data-testid="payment-type-filter"
              >
                <option value="All">All Payments</option>
                <option value="Quote">From Quotes</option>
                <option value="Invoice">From Invoices</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {hasActiveFilters ? 'Filtered Total' : 'Total Collected'}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currency.format(filteredStats.total)}</div>
            {hasActiveFilters && (
              <p className="text-xs text-muted-foreground mt-1">
                of {currency.format(stats.total)} total
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">From Quotes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currency.format(filteredStats.quote)}</div>
            {hasActiveFilters && (
              <p className="text-xs text-muted-foreground mt-1">
                of {currency.format(stats.quote)} total
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">From Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currency.format(filteredStats.invoice)}</div>
            {hasActiveFilters && (
              <p className="text-xs text-muted-foreground mt-1">
                of {currency.format(stats.invoice)} total
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {hasActiveFilters ? 'Filtered Payments' : 'All Payments'}
              </CardTitle>
              <CardDescription>
                {hasActiveFilters 
                  ? `Showing ${filteredPayments.length} of ${payments.length} payments`
                  : 'Complete payment history from all sources'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPayments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasActiveFilters 
                ? 'No payments found matching your filters' 
                : 'No payments recorded yet'
              }
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatPaymentDate(payment.date)}</TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-medium text-primary hover:underline flex items-center gap-1"
                        onClick={() => {
                          if (payment.type === 'Quote') {
                            navigate(`/quotes/${payment.source_id}`);
                          } else if (payment.type === 'Invoice') {
                            navigate(`/invoices/${payment.source_id}`);
                          }
                        }}
                        data-testid={`payment-link-${payment.source_id}`}
                      >
                        {payment.source_number}
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(payment.amount, payment.currency)}</TableCell>
                    <TableCell className="capitalize">{payment.method}</TableCell>
                    <TableCell className="text-muted-foreground">{payment.reference || 'N/A'}</TableCell>
                    <TableCell>
                      {payment.created_by_name || (() => {
                        const member = teamMembersForCreatedBy.find(m => m.id === payment.created_by);
                        return member ? member.name : 'Unknown';
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
